import { ArrowLeft, Eye, EyeOff, LockKeyhole, LogIn, ShieldCheck } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Brand } from "../../components/Brand";
import { useAuth } from "../../context/AuthContext";
import { useAdminToast } from "../../context/AdminToastContext";
import { adminErrorMessage, missingFieldsMessage } from "../../lib/adminFeedback";

export function AdminLogin({ unauthorized }: { unauthorized: boolean }) {
  const { login, logout, session, updatePassword } = useAuth();
  const { showToast } = useAdminToast();
  const [email, setEmail] = useState(session?.user.email || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const isInvite = window.location.hash.includes("type=invite") || window.location.hash.includes("type=recovery");

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      if (isInvite) {
        if (!session) {
          showToast({ type: "error", title: "Link nicht mehr gültig", message: "Öffne den Einladungs- oder Wiederherstellungslink erneut." });
          return;
        }
        if (newPassword.length < 8) {
          showToast({ type: "warning", title: "Passwort zu kurz", message: "Das neue Passwort muss mindestens 8 Zeichen enthalten." });
          return;
        }
        await updatePassword(newPassword);
        setMessage("Passwort gespeichert. Der Zugang wird geprüft…");
        showToast({ type: "success", title: "Passwort gespeichert", message: "Dein Admin-Zugang ist jetzt eingerichtet." });
        window.location.replace("/admin");
      } else {
        const missing = [!email.trim() && "E-Mail-Adresse", !password && "Passwort"].filter((field): field is string => Boolean(field));
        if (missing.length > 0) {
          const feedback = missingFieldsMessage(missing);
          setMessage(feedback);
          showToast({ type: "warning", title: "Anmeldung unvollständig", message: feedback });
          return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
          const feedback = "Bitte gib eine vollständige E-Mail-Adresse ein.";
          setMessage(feedback);
          showToast({ type: "warning", title: "E-Mail-Adresse ungültig", message: feedback });
          return;
        }
        await login(email, password);
        showToast({ type: "success", title: "Anmeldung erfolgreich", message: "Willkommen im Adminbereich des TTC Rehlingen." });
      }
    } catch (error) {
      const feedback = adminErrorMessage(error, "Anmeldung fehlgeschlagen.");
      setMessage(feedback);
      showToast({ type: "error", title: isInvite ? "Passwort nicht gespeichert" : "Anmeldung fehlgeschlagen", message: feedback });
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="login-page">
      <div className="login-visual">
        <img src="/images/hero-table-tennis.png" alt="" />
        <div className="login-visual-overlay" />
        <Link to="/" className="login-back"><ArrowLeft size={17} /> Zur Website</Link>
        <div className="login-visual-copy"><span className="eyebrow eyebrow--light"><span /> Vereinsverwaltung</span><h1>Alles Wichtige.<br />An einem Ort.</h1><p>News veröffentlichen, Trainingszeiten aktualisieren und Vereinsinhalte gemeinsam pflegen.</p></div>
      </div>
      <div className="login-panel">
        <div className="login-box">
          <Brand />
          <div className="login-title"><span><LockKeyhole /></span><div><h2>{isInvite ? "Zugang einrichten" : "Willkommen zurück"}</h2><p>{isInvite ? "Lege ein sicheres Passwort für deinen Admin-Zugang fest." : "Melde dich mit deinem Admin-Konto an."}</p></div></div>
          {unauthorized && <div className="form-alert form-alert--warning"><ShieldCheck /> <div><strong>Kein Admin-Zugriff</strong><span>Dieses Konto ist angemeldet, steht aber nicht auf der Admin-Liste.</span></div></div>}
          <form onSubmit={submit} noValidate className="login-form">
            {!isInvite && <><label>E-Mail-Adresse<input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="name@ttcrehlingen.de" /></label><label>Passwort<span className="password-input"><input type={showPassword ? "text" : "password"} autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="••••••••••" /><button type="button" onClick={() => setShowPassword((value) => !value)} aria-label="Passwort anzeigen">{showPassword ? <EyeOff /> : <Eye />}</button></span></label></>}
            {isInvite && <label>Neues Passwort<span className="password-input"><input type={showPassword ? "text" : "password"} autoComplete="new-password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} placeholder="Mindestens 8 Zeichen" /><button type="button" onClick={() => setShowPassword((value) => !value)} aria-label="Passwort anzeigen">{showPassword ? <EyeOff /> : <Eye />}</button></span></label>}
            {message && <p className="form-message">{message}</p>}
            <button className="button button--accent login-submit" disabled={busy}>{busy ? "Bitte warten…" : isInvite ? "Passwort speichern" : "Sicher anmelden"}<LogIn size={18} /></button>
          </form>
          {unauthorized && <button className="login-switch" onClick={() => void logout()}>Mit einem anderen Konto anmelden</button>}
          <p className="login-security"><ShieldCheck /> Geschützter Bereich für berechtigte Vorstands- und Redaktionsteams.</p>
        </div>
      </div>
    </main>
  );
}
